import type { ComponentPropsWithoutRef } from 'react'
import { MinusIcon, PlusIcon } from '#icons'
import { cn } from '#utils'

/**
 * QuantityStepper
 *
 * `− value +` control. Native buttons and an `<output>`, no state of its own:
 * the caller owns `value`. Colours are neutral; style per surface through the
 * class props.
 */
export interface QuantityStepperProps
  extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
  value: number
  /** Inclusive lower bound. */
  min?: number
  /** Inclusive upper bound. */
  max?: number
  onChange: (next: number) => void
  disabled?: boolean
  /** Classes for both step buttons. */
  buttonClassName?: string
  /** Classes for the value `<output>`. */
  valueClassName?: string
  decreaseLabel?: string
  increaseLabel?: string
  decreaseTestId?: string
  increaseTestId?: string
  valueTestId?: string
}

export function QuantityStepper({
  value,
  min = 1,
  max,
  onChange,
  disabled = false,
  className,
  buttonClassName,
  valueClassName,
  decreaseLabel = 'Decrease quantity',
  increaseLabel = 'Increase quantity',
  decreaseTestId,
  increaseTestId,
  valueTestId,
  ...props
}: QuantityStepperProps) {
  const stepClass = cn(
    'flex items-center justify-center rounded-full text-fg-primary transition-colors disabled:cursor-not-allowed disabled:opacity-40',
    buttonClassName,
  )

  return (
    <div className={cn('flex items-center', className)} {...props}>
      <button
        type="button"
        className={stepClass}
        aria-label={decreaseLabel}
        data-testid={decreaseTestId}
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
      >
        <MinusIcon className="size-5" />
      </button>
      <output
        className={cn('text-center tabular-nums text-fg-primary', valueClassName)}
        aria-live="polite"
        data-testid={valueTestId}
      >
        {value}
      </output>
      <button
        type="button"
        className={stepClass}
        aria-label={increaseLabel}
        data-testid={increaseTestId}
        disabled={disabled || (max !== undefined && value >= max)}
        onClick={() => onChange(value + 1)}
      >
        <PlusIcon className="size-5" />
      </button>
    </div>
  )
}
